import React from 'react';
import { Typography, Box } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
// Import your logos
import h2oLogo from '../assets/logos/h2o-logo.jpeg';
import datarobotLogo from '../assets/logos/datarobot-logo.jpeg';
import llmLogo from '../assets/logos/llm-ventures-logo.png';
import vwLogo from '../assets/logos/vw-group-logo.jpeg';
import vwgsLogo from '../assets/logos/vwgs-logo.jpeg';
import ptbLogo from '../assets/logos/ptb-logo.jpeg';

// Core interfaces for content structure
interface RichTextItem {
  type: 'text' | 'bold' | 'link';
  content: string;
  url?: string;
}

interface ListItem {
  content: RichTextItem[];
  //subItems?: (string | ListItem)[];
  subItems?: Array<string | RichTextItem[] | ListItem>;

}

interface ExperienceItem {
  company: string;
  logo?: string;
  role: string;
  period: string;
  description: string | (string | ListItem)[];
  link?: string;
  location?: string;
  isWebsite?: boolean;
  appStoreLinks?: {
    apple?: string;
    google?: string;
  };
  github?: {
    owner: string;
    repo: string;
  };
}

const NeuralCompanyNode: React.FC<{ company: string; logo?: string }> = ({ company, logo }) => {
  // Function to generate neural network colors
  const getNeuralColor = (name: string) => {
    const colors = [
      '#00D4FF', // Electric blue
      '#00FF88', // Neon green
      '#FF6B6B', // Neural red
      '#4ECDC4', // Neural teal
      '#45B7D1', // Neural light blue
      '#96CEB4', // Neural mint
      '#FFEAA7', // Neural yellow
      '#DDA0DD'  // Neural purple
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  const neuralColor = getNeuralColor(company);

  if (logo) {
    return (
      <Box
        component="img"
        className="no-print"
        src={logo}
        alt={`${company} logo`}
        sx={{
          width: 56,
          height: 56,
          objectFit: 'contain',
          borderRadius: '8px',
          background: 'rgba(0, 0, 0, 0.3)',
          p: 1,
          display: { xs: 'none', sm: 'block' },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'scale(1.02)',
          }
        }}
      />
    );
  }

  return (
    <Box
      className="no-print"
      sx={{
        width: 56,
        height: 56,
        borderRadius: '8px',
        background: `linear-gradient(135deg, ${neuralColor}60 0%, ${neuralColor}40 100%)`,
        display: { xs: 'none', sm: 'flex' },
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
        fontSize: '24px',
        fontWeight: 600,
        fontFamily: '"JetBrains Mono", monospace',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'scale(1.02)',
        }
      }}
    >
      {company.charAt(0)}
    </Box>
  );
};

const RichText: React.FC<{ content: string | RichTextItem[] | ListItem }> = ({ content }) => {
  if (typeof content === 'string') {
    return <Typography variant="body2" component="span">{content}</Typography>;
  }

  if (Array.isArray(content)) {
    // Handle RichTextItem[]
    return (
      <>
        {content.map((item, idx) => (
          <RichTextContent key={idx} item={item} />
        ))}
      </>
    );
  }

  if ('content' in content) {
    // Handle ListItem
    return (
      <>
        {content.content.map((item, idx) => (
          <RichTextContent key={idx} item={item} />
        ))}
        {content.subItems && (
          <Box component="ul" sx={{ pl: 2 }}>
            {content.subItems.map((subItem, idx) => (
              <li key={idx}>
                <RichText content={subItem} />
              </li>
            ))}
          </Box>
        )}
      </>
    );
  }

  return null;
};

// Separate component for rendering individual RichTextItems
const RichTextContent: React.FC<{ item: RichTextItem }> = ({ item }) => {
  switch (item.type) {
    case 'bold':
      return (
        <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
          {item.content}
        </Typography>
      );
    case 'link':
      return (
        <Typography variant="body2" component="span">
          <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#00D4FF' }}>
            {item.content}
          </a>
        </Typography>
      );
    default:
      return (
        <Typography variant="body2" component="span">
          {item.content}
        </Typography>
      );
  }
};

export const Experience: React.FC = () => {
  const experiences: ExperienceItem[] = [
    {
      company: 'Meta',
      role: 'Machine Learning Engineer (Contract)',
      period: 'September 2025 - December 2025',
      description: [
        'Fixed-scope on-device LLM inference research engagement, delivered in full: took small language models from fine-tuning to on-device deployment for several NLP use cases, including chat summarization, topic detection and association recognition.',
        'Worked on fine-tuning, model management and versioning, on-device deployment, and evaluation of quality and latency on mobile hardware.'
      ]
    },
    {
      company: 'LLM Ventures',
      logo: llmLogo,
      role: 'Founder',
      period: 'February 2025 - Present',
      description: [
        'Design, build and ship LLM products end to end, covering architecture, agents, evals, deployment and user support.',
        { content: [
          { type: 'bold', content: 'PocketPal AI' },
          { type: 'text', content: ' (' },
          { type: 'link', content: 'pocketpal.dev', url: 'https://pocketpal.dev/' },
          { type: 'text', content: ', ' },
          { type: 'link', content: 'GitHub', url: 'https://github.com/a-ghorbani/pocketpal-ai' },
          { type: 'text', content: '): open-source mobile app (React Native / TypeScript, llama.cpp) that runs LLMs fully on-device. Launched as a side project in October 2024; 8k+ GitHub stars, 850+ forks, released on the App Store and Google Play, with a model hub (' },
          { type: 'link', content: 'PalsHub', url: 'https://palshub.ai/' },
          { type: 'text', content: ') and public ' },
          { type: 'link', content: 'benchmarks', url: 'https://pocketpal.dev/leaderboard' },
          { type: 'text', content: '.' }
        ] },
        'Agentic engineering: build tool-calling / ReAct agents (web search and URL reading in PocketPal) and run multi-agent Claude Code development teams with custom agents, skills and plugins.',
        { content: [
          { type: 'link', content: 'agistry', url: 'https://github.com/a-ghorbani/agistry' },
          { type: 'text', content: ': multi-agent coordination service. Claude Code sessions on related projects or shared resources register their task and role, and hand off work through a durable mailbox. An MCP channel pushes messages into running sessions with at-least-once delivery.' }
        ] },
        { content: [
          { type: 'text', content: 'Evaluation: designed reproducible benchmarks, published as public leaderboards on pocketpal.dev: ' },
          { type: 'link', content: 'ferret-bench', url: 'https://pocketpal.dev/evals/web-search' },
          { type: 'text', content: ' (agentic web search with small LLMs: config ablations and a model leaderboard) and ' },
          { type: 'link', content: 'xebench', url: 'https://pocketpal.dev/leaderboard/engines' },
          { type: 'text', content: ' (the same model and prompt compared across llama.cpp, ExecuTorch and LiteRT-LM on CPU/GPU/NPU).' }
        ] }
      ],
      appStoreLinks: {
        apple: 'https://apps.apple.com/us/app/pocketpal-ai/id6502579498',
        google: 'https://play.google.com/store/apps/details?id=com.pocketpalai'
      },
      github: {
        owner: 'a-ghorbani',
        repo: 'pocketpal-ai'
      }
    },
    {
      company: 'H2O.ai, Inc.',
      logo: h2oLogo,
      role: 'Lead Data Scientist / Solution Engineer',
      period: 'July 2020 - February 2025',
      description: [
        'Primary technical advisor to enterprise customers (financial services, manufacturing) from discovery and pilot through production on H2O AI Cloud and h2oGPTe.',
        'Partnered with account executives across complex enterprise sales cycles. Translated business requirements into solution architectures and presented to audiences ranging from C-level to engineering and IT.',
        'Led GenAI engagements covering RAG, agents, LLM fine-tuning and evaluation frameworks for measuring LLM quality on customer use cases. Fed field insights back to product and engineering.',
        'Ran customer workshops and technical enablement, and directed ML pilots in regression, classification, time series, NLP and computer vision.',
        {
          content: [{ type: 'bold', content: 'Key Contributions' }],
          subItems: [
            'Agentic RAG for a top-10 global bank: replaced the bank\'s existing RAG system (Microsoft stack, OpenAI models), which scored ~70% on its own benchmark, with an agentic pipeline: VLM-based document parsing (table and chart parsing errors cut by 80% and 50%), structured storage and query routing. Reached 95% on the bank\'s validation set.',
            [
              {type: 'text', content: 'GPTeller: Developed a prototype LLM-based banking customer agent to understand queries, extract information, and align with internal bank policies to follow the processes. '},
              {type: 'link', content: 'Try it out', url: 'https://genai.h2o.ai/appstore?q=gpteller'},
            ],
            'Y.O.D.A. Inspection System: Created an automated document validation tool leveraging LLMs for high-precision sectors.',
            [
              { type: 'text', content: 'Classic ML for industry: ' },
              { type: 'link', content: 'predictive maintenance', url: 'https://h2o.ai/blog/2021/ai-driven-predictive-maintenance-with-h2o-hybrid-cloud/' },
              { type: 'text', content: ', a three-layer ' },
              { type: 'link', content: 'fraud detection', url: 'https://h2o.ai/blog/2023/building-a-fraud-detection-model-with-h2o-ai-cloud/' },
              { type: 'text', content: ' system, and AutoML for ' },
              { type: 'link', content: 'reducing false positives', url: 'https://h2o.ai/blog/2023/reducing-false-positives-in-financial-transactions-with-automl/' },
              { type: 'text', content: ' in financial transactions.' }
            ]
          ]
        }
      ]
    },
    {
      company: 'DataRobot, Inc.',
      logo: datarobotLogo,
      role: 'Data Scientist (customer facing)',
      period: 'October 2018 - June 2020',
      description: [
        'Technical lead on 10+ enterprise proofs of concept across banking, insurance, manufacturing and logistics (incl. a top-5 global logistics company), plus large consultancies onboarded as partners. Around 8 became long-term customers.',
        'Mentored and enabled customer data science teams to put automated ML into production for high-ROI use cases.'
      ]
    },
    {
      company: 'Volkswagen AG - Data Lab',
      logo: vwLogo,
      role: 'Data Scientist/Big Data Expert',
      period: 'July 2016 - September 2018',
      description: [
        'Led a cross-functional team of 6 to architect and build an in-house data science platform on AWS, Cloudera, Open Telekom Cloud and Kubernetes.',
        'ML at scale: Spark MLlib demand model covering 10 years of parts across VW Group\'s major markets.',
        'Interpretable ML: churn-driver dashboard for millions of customers (1B+ rows, GPU database, XGBoost, partial dependence).'
      ]
    },
    {
      company: 'Volkswagen Group - AutoVision GmbH',
      logo: vwgsLogo,
      role: 'Data Scientist',
      period: 'August 2015 - June 2016',
      description: [
        'Predictive modelling (customer churn, driving-style patterns, inventory anomaly detection) and big data integration, turning results into business countermeasures.'
      ]
    },
    {
      company: 'Earlier: Research & Engineering',
      logo: ptbLogo,
      role: 'PTB · TUM · Pezhvak',
      period: '2006 - 2015',
      description: [
        { content: [
          { type: 'bold', content: 'Researcher, Physikalisch-Technische Bundesanstalt (2009–2015): ' },
          { type: 'text', content: 'new algorithm for transient probability density functions using stochastic processes and Monte Carlo methods.' }
        ] },
        { content: [
          { type: 'bold', content: 'Guest Scientist, TU Munich (2008–2009): ' },
          { type: 'text', content: 'probabilistic real-time automata for data mining.' }
        ] },
        { content: [
          { type: 'bold', content: 'Performance Analyst, Pezhvak, Tehran (2006–2008): ' },
          { type: 'text', content: 'throughput, latency and load-tolerance analysis of a Java EE enterprise workflow application.' }
        ] }
      ]
    }
  ];

  // Roles start collapsed to their terminal line; hovering peeks into the content (print always shows all)
  const [openRoles, setOpenRoles] = React.useState<Set<number>>(() => new Set());
  const toggleRole = (index: number) => {
    setOpenRoles((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  // One-time staggered peek when the section first scrolls into view, hinting that roles expand.
  // Skipped for reduced-motion users and after the first time in a session.
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const [introPeek, setIntroPeek] = React.useState(false);
  React.useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    try {
      if (sessionStorage.getItem('experiencePeekShown')) return;
    } catch { /* storage unavailable: still show the hint */ }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      try { sessionStorage.setItem('experiencePeekShown', '1'); } catch { /* ignore */ }
      setIntroPeek(true);
      timer = setTimeout(() => setIntroPeek(false), 1400);
    }, { threshold: 0.25 });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <Box ref={sectionRef}>
      <Typography variant="h4" gutterBottom sx={{
        mb: 4,
        color: '#FFFFFF',
        fontFamily: '"Inter", sans-serif'
      }}>
        Experience
      </Typography>
      {experiences.map((exp, index) => {
        const isOpen = openRoles.has(index);
        const peeking = !isOpen && introPeek;
        return (
        <React.Fragment key={index}>
          <Box className="print-entry" sx={{
            display: 'flex',
            gap: { xs: 0, sm: 3 },
            mb: isOpen ? 4 : 1.5,
            background: isOpen ? 'rgba(11, 20, 38, 0.6)' : 'transparent',
            border: isOpen ? '1px solid rgba(0, 212, 255, 0.2)' : '1px solid transparent',
            borderRadius: '12px',
            p: isOpen ? { xs: 1.5, sm: 3 } : 0,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': isOpen ? {
              transform: 'translateY(-2px)',
              background: 'rgba(11, 20, 38, 0.8)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
            } : {},
            // Peek: on hover-capable devices, a collapsed role slides partly open with a fade
            '@media (hover: hover)': isOpen ? {} : {
              '&:hover .exp-details': {
                maxHeight: '96px',
                opacity: 1,
                mt: 1.5,
              },
            },
          }}>
            {isOpen && <NeuralCompanyNode company={exp.company} logo={exp.logo} />}

            {/* Content */}
            <Box sx={{
              flex: 1,
              width: { xs: '100%', sm: 'auto' },
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
            }}>
              {/* Terminal-style header: tap to expand/collapse the role */}
              <Box
                className="no-print"
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => toggleRole(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleRole(index);
                  }
                }}
                sx={{
                  mb: isOpen ? 2 : 0,
                  p: 1.5,
                  background: 'rgba(0, 0, 0, 0.4)',
                  borderRadius: '6px',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                  flexWrap: 'wrap',
                  '&:hover': { borderColor: 'rgba(0, 212, 255, 0.6)' },
                  '&:focus-visible': { outline: '2px solid #00D4FF', outlineOffset: '2px' },
                }}
              >
                <Typography component="span" sx={{ color: '#00D4FF', fontFamily: 'inherit', fontSize: 'inherit', width: '1ch' }}>
                  {isOpen ? '▾' : '▸'}
                </Typography>
                <Typography component="span" sx={{ color: '#00FF88', fontFamily: 'inherit', fontSize: 'inherit' }}>
                  $ cat {exp.company.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}.json
                </Typography>
                {!isOpen && (
                  <Typography component="span" sx={{ color: '#78909C', fontFamily: 'inherit', fontSize: 'inherit' }}>
                    # {exp.role} · {exp.period}
                  </Typography>
                )}
              </Box>

              <Box
                className="exp-details"
                onClick={isOpen ? undefined : () => toggleRole(index)}
                sx={{
                  overflow: 'hidden',
                  maxHeight: isOpen ? 'none' : peeking ? '96px' : 0,
                  opacity: isOpen || peeking ? 1 : 0,
                  mt: peeking ? 1.5 : 0,
                  transitionDelay: peeking ? `${index * 80}ms` : '0ms',
                  px: isOpen ? 0 : 1.5,
                  cursor: isOpen ? 'auto' : 'pointer',
                  transition: 'max-height 0.35s ease, opacity 0.35s ease, margin 0.35s ease',
                  ...(isOpen ? {} : {
                    WebkitMaskImage: 'linear-gradient(to bottom, #000 30%, transparent)',
                    maskImage: 'linear-gradient(to bottom, #000 30%, transparent)',
                  }),
                  '@media print': {
                    maxHeight: 'none !important',
                    opacity: '1 !important',
                    padding: '0 !important',
                    WebkitMaskImage: 'none !important',
                    maskImage: 'none !important',
                  },
                }}
              >

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#FFFFFF',
                  mb: 0.5,
                  fontFamily: '"Inter", sans-serif'
                }}
              >
                {exp.company}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: '#00D4FF',
                  fontWeight: 500,
                  mb: 0.5,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '16px'
                }}
              >
                <span className="no-print">&gt; </span>{exp.role}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#B0BEC5',
                  fontSize: '13px',
                  mb: 2,
                  fontWeight: 500,
                  fontFamily: '"JetBrains Mono", monospace'
                }}
              >
                <span className="no-print">[</span>{exp.period}<span className="no-print">]</span>{exp.location && ` @ ${exp.location}`}
              </Typography>

              {Array.isArray(exp.description) ? (
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: { xs: 2, sm: 3 },
                    '& li': {
                      color: '#B0BEC5',
                      fontSize: '14px',
                      mb: 1,
                      lineHeight: 1.6,
                      fontFamily: '"Inter", sans-serif',
                    },
                    '& li ul': {
                      mt: 1,
                      mb: 1.5,
                    },
                    '& li::marker': {
                      color: '#00FF88',
                    }
                  }}
                >
                  {exp.description.map((item, i) => (
                    <li key={i}>
                      {typeof item === 'string' ? (
                        <Typography variant="body1" component="span" sx={{ fontSize: '14px', lineHeight: 1.6, color: '#B0BEC5' }}>{item}</Typography>
                      ) : (
                        <RichText content={item} />
                      )}
                    </li>
                  ))}
                </Box>
              ) : (
                <Typography variant="body1" sx={{
                  color: '#B0BEC5',
                  fontSize: '14px',
                  lineHeight: 1.6,
                }}>
                  {exp.description}
                </Typography>
              )}

              {exp.appStoreLinks && (
                <Box className="no-print" sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 2,
                  alignItems: 'center',
                  '& a': {
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                    }
                  }
                }}>
                  {exp.appStoreLinks.apple && (
                    <a href={exp.appStoreLinks.apple} target="_blank" rel="noopener noreferrer">
                      <img
                        src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                        alt="Download on the App Store"
                        style={{
                          height: '40px',
                          width: 'auto',
                          objectFit: 'contain'
                        }}
                      />
                    </a>
                  )}
                  {exp.appStoreLinks.google && (
                    <a href={exp.appStoreLinks.google} target="_blank" rel="noopener noreferrer">
                      <img
                        src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                        alt="Get it on Google Play"
                        style={{
                          height: '80px',
                          width: 'auto',
                          objectFit: 'contain',
                          transform: 'scale(0.7)',
                          transformOrigin: 'left center'
                        }}
                      />
                    </a>
                  )}
                </Box>
              )}

              {exp.link && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <a
                    href={`https://${exp.link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'inherit',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {exp.isWebsite ? (
                      <Typography variant="body2" sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'text.primary'
                        }
                      }}>
                        🌐 {exp.link}
                      </Typography>
                    ) : (
                      <GitHubIcon sx={{
                        fontSize: 20,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'text.primary'
                        }
                      }} />
                    )}
                  </a>
                  {exp.github && (
                    <img
                      src={`https://img.shields.io/github/stars/${exp.github.owner}/${exp.github.repo}?style=social`}
                      alt="GitHub stars"
                      style={{ height: 20 }}
                    />
                  )}
                </Box>
              )}
              </Box>
            </Box>
          </Box>
        </React.Fragment>
        );
      })}
    </Box>
  );
}
